import type React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppVersionUpdate } from "@/contexts"
import { FONT_WEIGHT } from "@/models/typography"

import CloseIcon from "@mui/icons-material/Close"
import { Button, IconButton, Snackbar, Stack, Typography } from "@mui/material"
import type { SnackbarCloseReason } from "@mui/material"

export const AppVersionUpdateNotification: React.FC = () => {
  const { t } = useTranslation("common")
  const { dismissUpdatePrompt, isUpdateAvailable, reloadToLatestVersion } =
    useAppVersionUpdate()
  const [isReloading, setIsReloading] = useState(false)

  const handleReload = async (): Promise<void> => {
    setIsReloading(true)

    try {
      await reloadToLatestVersion()
    } finally {
      setIsReloading(false)
    }
  }

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return
    }

    dismissUpdatePrompt()
  }

  return (
    <Snackbar
      anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      autoHideDuration={null}
      message={
        <>
          <Typography
            sx={{
              fontWeight: FONT_WEIGHT.bold,
            }}
          >
            {t("appUpdatePrompt.title")}
          </Typography>
          <Typography variant="body2">
            {t("appUpdatePrompt.description")}
          </Typography>
        </>
      }
      onClose={handleSnackbarClose}
      open={isUpdateAvailable}
      action={
        <Stack direction="row" spacing={1}>
          <Button
            color="primary"
            disabled={isReloading}
            onClick={() => {
              void handleReload()
            }}
            size="small"
            variant="contained"
          >
            {t("button.reload")}
          </Button>
          <IconButton
            aria-label={t("button.close")}
            color="inherit"
            disabled={isReloading}
            onClick={dismissUpdatePrompt}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      }
      sx={{
        alignItems: "center",
        width: "100%",
        "& .MuiSnackbarContent-root": {
          backgroundColor: (theme) => theme.palette.primary.dark,
        },
      }}
    ></Snackbar>
  )
}
